import { useState } from 'react';
import { подписьСсылки } from '../ui/notice-links';
import { когдаПришло } from '../support/kogda';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { K } from '../kabinet/atoms';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * КОЛОКОЛЬЧИК В ШАПКЕ (передача, п. 7).
 *
 * Счётчик — число непрочитанных; «Прочитать все» гасит его.
 *
 * РАСКРЫТИЕ ЧЕРЕЗ `<details>`, А НЕ ЧЕРЕЗ СОСТОЯНИЕ И ПОДЛОЖКУ.
 * Тег умеет всё нужное сам: закрывается по Escape у поддерживающих
 * браузеров, попадает в обход клавишей Tab, не требует ловли нажатия
 * мимо. Своя реализация того же — это триста строк, из которых сто
 * про доступность и они будут написаны последними.
 *
 * ПРОЧИТАННЫМ СЧИТАЕТСЯ ОТКРЫТОЕ, А НЕ ПОКАЗАННОЕ. Гасить счётчик
 * при открытии списка значит терять сообщение, на которое человек
 * взглянул мельком: он его больше не найдёт, а оно было важным.
 * ═══════════════════════════════════════════════════════════════════════
 */
interface Item {
  id: string;
  kind: 'works' | 'important' | 'sale' | 'service';
  title: string;
  text: string;
  url: string | null;
  /**
   * Как ссылка подписана руками отправителя. `null` — подписать
   * по списку известных мест (`ui/notice-links.ts`).
   *
   * Служба это поле отдаёт давно (`BellItem.linkText`), а колокольчик
   * его не читал: набранная сотрудником подпись стояла в предпросмотре
   * админки и пропадала по дороге к человеку. Сторож на оба конца —
   * `admin/pravki-po-skrinam.spec.ts`.
   */
  linkText: string | null;
  sentAt: string;
  readAt: string | null;
}

/**
 * ═══════════════════════════════════════════════════════════════════════
 * НЕЗНАКОМЫЙ ВИД СООБЩЕНИЯ НЕ ДОЛЖЕН ГАСИТЬ КАБИНЕТ.
 *
 * `TONE[n.kind].wash` на виде, которого здесь нет, — это
 * `Cannot read properties of undefined`, то есть БЕЛЫЙ ЭКРАН вместо
 * всего кабинета. Причём поймать это можно только тем, что служба
 * однажды пришлёт новый вид: разметка, типы и проверки будут зелёными
 * до самого этого дня.
 *
 * Так и вышло 26.08 на пробе: в образец попал вид `ops`, и кабинет
 * не собрался вовсе — ни шапки, ни таблицы, ни сообщения об ошибке.
 *
 * Поэтому неизвестное показывается как «Сервис», а не роняет экран:
 * незнакомое сообщение всё равно надо ПОКАЗАТЬ — оно для человека,
 * а не для нас.
 * ═══════════════════════════════════════════════════════════════════════
 */
function тон(kind: string): { wash: string; ink: string; title: string } {
  return TONE[kind as Item['kind']] ?? TONE.service;
}

/**
 * ═══════════════════════════════════════════════════════════════════════
 * ЦВЕТА ВИДОВ — ТЕ ЖЕ, ЧТО ВИДЕЛ ОТПРАВИТЕЛЬ.
 *
 * Здесь они разошлись с админкой на двух видах из четырёх: «скидка»
 * была зелёной вместо цвета марки, а «сервис» — СЕРЫМ ПОЛЕМ фона
 * (`--bg-sunken`) вместо зелёного. Из-за последнего живое уведомление
 * пришло владельцу серым кирпичом, тогда как в предпросмотре админки
 * оно выглядело нарядным, — и он написал ровно это: «выглядит красиво,
 * а пришло какое-то ужасное».
 *
 * Правда у отправителя: `admin/notices.tab.css`, `.nt-метка-*`.
 * Сторож на оба конца — `layout/kolokolchik.spec.ts`.
 * ═══════════════════════════════════════════════════════════════════════
 */
const TONE: Record<Item['kind'], { wash: string; ink: string; title: string }> = {
  works: { wash: 'var(--warn-wash)', ink: 'var(--warn-ink)', title: 'Работы' },
  important: { wash: 'var(--bad-wash)', ink: 'var(--bad-ink)', title: 'Важно' },
  sale: { wash: 'var(--brand-wash)', ink: 'var(--brand)', title: 'Скидка' },
  service: { wash: 'var(--good-wash)', ink: 'var(--good-ink)', title: 'Сервис' },
};

/** «1 новое», «2 новых», «5 новых» — число рядом с неверным словом
    читается как опечатка сервиса. */
function новых(n: number): string {
  const сто = n % 100;
  const десять = сто % 10;
  const слово = сто > 10 && сто < 20 ? 'новых' : десять === 1 ? 'новое' : десять > 1 && десять < 5 ? 'новых' : 'новых';
  return `${n} ${слово}`;
}

export function Bell({ wsId }: { wsId: string }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  const q = useQuery({
    queryKey: ['notices', wsId],
    queryFn: () => api<{ items: Item[]; unread: number }>(`/workspaces/${wsId}/notices`),
    enabled: Boolean(wsId),
    retry: false,
    // Раз в пять минут: сообщения приходят редко, а лишний запрос
    // на каждом переходе между экранами — это лишний запрос
    staleTime: 5 * 60_000,
  });

  const readAll = useMutation({
    mutationFn: () => api(`/workspaces/${wsId}/notices/read-all`, { method: 'POST', body: {} }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['notices', wsId] }),
  });

  const read = useMutation<unknown, Error, string>({
    mutationFn: (id) => api(`/workspaces/${wsId}/notices/${id}/read`, { method: 'POST', body: {} }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['notices', wsId] }),
  });

  // ОТКАЗ МОЛЧИТ. Колокольчик с восклицательным знаком вместо числа
  // сообщает о нашей поломке человеку, который ничего с ней не сделает
  if (q.error || q.data === undefined) return null;

  const { items, unread } = q.data;

  return (
    <details
      className="relative"
      open={open}
      onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}
    >
      <summary
        className={K.кнопкаУведомлений}
        data-d="кнопка-уведомлений"
        style={{ listStyle: 'none', position: 'relative', cursor: 'pointer' }}
        title="Сообщения сервиса"
        aria-label={unread > 0 ? `Сообщения сервиса, непрочитанных ${unread}` : 'Сообщения сервиса'}
      >
        <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
          <path d="M4 6.6a4 4 0 0 1 8 0v2.2l1.1 2H2.9L4 8.8z" strokeLinejoin="round" />
          <path d="M6.6 12.6a1.5 1.5 0 0 0 2.8 0" strokeLinecap="round" />
        </svg>

        {/* ЧИСЛО, А НЕ ТОЧКА. Точка говорит «что-то есть», число —
            «есть три»; второе человек закрывает, первое — терпит */}
        {unread > 0 && (
          <span
            data-d="счётчик-колокольчика"
            style={{
              position: 'absolute',
              top: 2,
              right: 0,
              minWidth: 16,
              height: 16,
              padding: '0 4px',
              borderRadius: 'var(--r-full)',
              background: 'var(--bad)',
              // Чернила на заливке тревоги: своя переменная, а не
              // белый числом — в тёмной теме белый по-прежнему белый,
              // а вот заливка другая
              color: 'var(--ink-on-brand)',
              fontSize: 10,
              lineHeight: '16px',
              textAlign: 'center',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </summary>

      <div
        className="rk-panel"
        style={{
          position: 'absolute',
          right: 0,
          top: 'calc(100% + 6px)',
          width: 340,
          maxWidth: '90vw',
          maxHeight: 420,
          overflowY: 'auto',
          zIndex: 40,
          padding: 'var(--sp-3)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-2)',
        }}
      >
        {/* ═══════════════════════════════════════════════════════════
            «УВЕДОМЛЕНИЯ», А НЕ «СООБЩЕНИЯ».

            Раздел, из которого они уходят, называется «Уведомления»
            (и в админке, и в письме, и в настройках почты). Здесь
            стояло «Сообщения» — то же самое другим словом, и человек,
            которому сказали «включите уведомления», ищет в кабинете
            уведомления и не находит.

            Рядом — сколько новых, как в предпросмотре отправителя:
            «1 новое». Кнопка «Прочитать все» ушла вниз списка: сверху
            она стояла на месте счётчика и отвечала не на тот вопрос.
            ═══════════════════════════════════════════════════════ */}
        <div className="rk-bell-head">
          <span className="rk-bell-name">Уведомления</span>
          {unread > 0 && <span className="rk-bell-new">{новых(unread)}</span>}
        </div>

        {items.length === 0 ? (
          <p className="rk-hint">Пока ничего не приходило.</p>
        ) : (
          items.map((n) => (
            /* ═══════════════════════════════════════════════════════
                КАРТОЧКА — ТА ЖЕ, ЧТО В ПРЕДПРОСМОТРЕ ОТПРАВИТЕЛЯ.

                Было: плашка вида и заголовок ОДНОЙ строкой — заголовок
                вставал справа от плашки, ужимался в треть ширины
                и переносился по слогам. У отправителя в тот же миг
                предпросмотр показывал заголовок отдельной строкой
                во всю ширину, и он был уверен, что клиент видит то же.

                Стало по предпросмотру, сверху вниз: вид и когда пришло,
                заголовок, текст, ссылка. Непрочитанное подложено цветом
                марки — тоже как там.
                ═══════════════════════════════════════════════════ */
            <article
              key={n.id}
              className={`rk-bell-card${n.readAt === null ? ' rk-bell-card-new' : ''}`}
            >
              <span className="flex items-center" style={{ gap: 'var(--sp-2)' }}>
                <span
                  className="rk-bell-tag"
                  style={{ background: тон(n.kind).wash, color: тон(n.kind).ink }}
                >
                  {тон(n.kind).title}
                </span>
                <span className="rk-bell-when">{когдаПришло(n.sentAt)}</span>
              </span>

              <span className="rk-bell-title">{n.title}</span>
              <span className="rk-bell-text">{n.text}</span>

              {/* ══════════════════════════════════════════════════
                  ССЫЛКА ПОДПИСАНА ТЕМ ЖЕ, ЧТО ВИДЕЛ ОТПРАВИТЕЛЬ.

                  Здесь стояло слово «открыть» — одно на все
                  уведомления. Сотрудник в админке при этом видел
                  в предпросмотре «Посмотреть тарифы →» и был уверен,
                  что клиент прочтёт то же самое.

                  Подписи живут в `ui/notice-links.ts` — одним куском
                  на оба конца: предпросмотр и колокольчик обязаны
                  показывать одно, иначе предпросмотр не предпросмотр.

                  ПОДПИСЬ, НАБРАННАЯ РУКАМИ, СИЛЬНЕЕ СПИСКА: `/tarify`
                  со своим «Успеть до пятницы» ушёл бы как «Посмотреть
                  тарифы», то есть не тем, что видел отправитель.
                  ══════════════════════════════════════════════════ */}
              {n.url !== null && (
                <a className="rk-bell-link" href={n.url}>
                  {подписьСсылки(n.url, n.linkText) ?? 'открыть'} →
                </a>
              )}

              {/* «Прочитано» — тихой строкой под текстом, а не рядом
                  со ссылкой: одно ведёт на экран, другое гасит метку,
                  и стоять им в одном ряду не за что. */}
              {n.readAt === null && (
                <button className="rk-bell-read" onClick={() => read.mutate(n.id)}>
                  прочитано
                </button>
              )}
            </article>
          ))
        )}

        {/* ПРОЧИТАТЬ ВСЕ — ПОД СПИСКОМ. Сверху она стояла на месте
            счётчика новых и отвечала не на тот вопрос: сначала человек
            смотрит, СКОЛЬКО пришло, и только прочитав — гасит. */}
        {unread > 0 && (
          <button
            className="rk-button-quiet rk-button-sm"
            style={{ alignSelf: 'flex-start' }}
            disabled={readAll.isPending}
            onClick={() => readAll.mutate()}
          >
            Прочитать все
          </button>
        )}
      </div>
    </details>
  );
}
